import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatError, DAYS, DAY_SHORT, ROLES } from "@/lib/api";

const DayToggles = ({ value, onChange, prefix }) => (
  <div className="grid grid-cols-4 gap-1.5">
    {DAYS.map((d) => {
      const on = value.includes(d);
      return (
        <button key={d} type="button" aria-pressed={on} data-testid={`${prefix}-day-${d}`}
          onClick={() => onChange(on ? value.filter((x) => x !== d) : DAYS.filter((x) => x === d || value.includes(x)))}
          className={`rounded-md border py-2 text-xs font-semibold transition-colors ${on ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-neutral-800 text-neutral-500 hover:border-neutral-600"}`}>
          {DAY_SHORT[d]}
        </button>
      );
    })}
  </div>
);

const Row = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-neutral-400">{label}</Label>
    {children}
  </div>
);

const inputCls = "h-10 border-neutral-800 bg-neutral-900";

export const EditRegistrationButton = ({ reg, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const start = async () => {
    setError("");
    const { data: vol } = await api.get(`/admin/registrations/${reg.id}/volunteer`).catch(() => ({ data: null }));
    setF({
      name: reg.name, email: reg.email, phone: reg.phone, party_size: reg.party_size, days: reg.days, opt_in: reg.opt_in,
      volunteer: reg.volunteer, volunteer_role: vol?.role || "", volunteer_days: vol?.days || reg.days, volunteer_notes: vol?.notes || "",
    });
    setOpen(true);
  };

  const conflicts = f ? Object.keys(reg.checkins || {}).filter((d) => !f.days.includes(d)) : [];

  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.days.length) return setError("Select at least one day attending.");
    if (f.volunteer && !f.volunteer_role) return setError("Choose a volunteer role.");
    setSaving(true);
    try {
      const payload = { ...f, party_size: Number(f.party_size) };
      if (!f.volunteer) ["volunteer_role", "volunteer_days", "volunteer_notes"].forEach((k) => delete payload[k]);
      const { data } = await api.put(`/admin/registrations/${reg.id}`, payload);
      toast.success("Registration updated");
      if (data.checkin_conflicts.length) toast.warning(`Check-in kept for ${data.checkin_conflicts.map((d) => DAY_SHORT[d]).join(", ")} (no longer planned)`);
      setOpen(false);
      onSaved();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button onClick={start} title="Edit" className="rounded p-1.5 text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-amber-400" data-testid={`edit-registration-${reg.id}`}>
        <Pencil className="h-4 w-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-neutral-800 bg-neutral-950 sm:max-w-lg" data-testid="edit-registration-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-gold">Edit registration</DialogTitle>
            <DialogDescription className="text-xs">Signed by “{reg.signature}” · acknowledgment unchanged</DialogDescription>
          </DialogHeader>
          {f && (
            <form onSubmit={save} className="space-y-4" data-testid="edit-registration-form">
              <Row label="Household / name"><Input value={f.name} onChange={set("name")} required className={inputCls} data-testid="edit-reg-name" /></Row>
              <div className="grid gap-4 sm:grid-cols-2">
                <Row label="Email"><Input type="email" value={f.email} onChange={set("email")} required className={inputCls} data-testid="edit-reg-email" /></Row>
                <Row label="Phone"><Input value={f.phone} onChange={set("phone")} required className={inputCls} data-testid="edit-reg-phone" /></Row>
              </div>
              <Row label="Party size (1–20)"><Input type="number" min={1} max={20} value={f.party_size} onChange={set("party_size")} required className={`${inputCls} w-28`} data-testid="edit-reg-party-size" /></Row>
              <Row label="Days attending"><DayToggles value={f.days} onChange={set("days")} prefix="edit-reg" /></Row>
              {conflicts.length > 0 && (
                <p className="flex gap-2 rounded-md border border-amber-600/50 bg-amber-950/40 p-3 text-xs text-amber-200" data-testid="edit-reg-checkin-conflict">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {conflicts.map((d) => DAY_SHORT[d]).join(", ")} already has a check-in. It will be kept and shown as “not planned”.
                </p>
              )}
              <label className="flex items-center gap-3 text-sm"><Checkbox checked={f.opt_in} onCheckedChange={(v) => set("opt_in")(!!v)} data-testid="edit-reg-optin" /> Future events opt-in</label>
              <label className="flex items-center gap-3 text-sm text-emerald-300">
                <Checkbox checked={f.volunteer} onCheckedChange={(v) => setF((p) => ({ ...p, volunteer: !!v, volunteer_days: p.volunteer_days.length ? p.volunteer_days : p.days }))} data-testid="edit-reg-volunteer" /> Volunteering
              </label>
              {f.volunteer && (
                <div className="space-y-4 rounded-md border border-emerald-800/60 p-3">
                  <Row label="Role">
                    <select value={f.volunteer_role} onChange={set("volunteer_role")} className="h-10 w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 text-sm" data-testid="edit-reg-volunteer-role">
                      <option value="">Choose…</option>
                      {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </Row>
                  <Row label="Days available"><DayToggles value={f.volunteer_days} onChange={set("volunteer_days")} prefix="edit-reg-vol" /></Row>
                  <Row label="Notes"><Textarea value={f.volunteer_notes} onChange={set("volunteer_notes")} className="min-h-[70px] border-neutral-800 bg-neutral-900" data-testid="edit-reg-volunteer-notes" /></Row>
                </div>
              )}
              {error && <p className="text-sm text-red-400" data-testid="edit-reg-error">{error}</p>}
              <button type="submit" disabled={saving} className="h-11 w-full rounded-full bg-amber-500 font-display font-semibold text-neutral-950 disabled:opacity-60" data-testid="edit-reg-save">
                {saving ? "Saving…" : "Save changes"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
